/**
 * Which fields on a payment or receipt line belong to which payment method.
 *
 * A line keeps every field it has ever held: the inputs for the methods it is not using are only
 * DISABLED, not emptied, and the form submits them all regardless of type. So picking Cheque,
 * entering a number and a date, then switching to Cash saves a cash line carrying a cheque number
 * and date — which then prints on the voucher and lands in the Excel export. Switching back the
 * other way silently keeps the OLD cheque number, which may belong to a book that has moved on.
 *
 * Shared by the AP payment and AR receipt forms so the two cannot disagree about what a method
 * change means.
 */

/** Fields only a cheque line has any use for. */
const CHEQUE_FIELDS = [
  'chequeNumber',
  'chequeDate',
  'chequeBookId',
  'endorsedChequeId',
  'crossingType',
  // The drawer's bank and branch, on a cheque someone hands us (AR only, GAP-014).
  'drawerBankId',
  'drawerBranchId',
] as const;

/** Methods that carry a reference from the acquirer or the bank. */
const CARD_REFERENCE_METHODS = ['CreditCard', 'DebitCard', 'BankTransfer', 'OnlineBanking'];

/**
 * The fields to blank when a line's payment method changes, as a patch to merge over the line.
 *
 * Only clears what the NEW method cannot use, so switching between two card methods keeps the
 * reference the operator already typed. The funding source clears itself: FundingSourceSelect
 * drops a selection the new method is not eligible for.
 */
export function clearedByMethodChange(newMethod: string): Record<string, string> {
  const patch: Record<string, string> = {};

  if (newMethod !== 'Cheque')
    for (const field of CHEQUE_FIELDS) patch[field] = '';

  if (!CARD_REFERENCE_METHODS.includes(newMethod))
    patch.cardReference = '';

  if (newMethod !== 'PettyCash')
    patch.pettyCashFloatId = '';

  return patch;
}
